import * as React from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";

import * as client from "../../../client";
import { readableLayer, perilCodeDisplayName } from "./util";
import { useFinancialModelAudit } from "../data";
import {
  ALL_AUDIT_SOURCES,
  FinancialModelAuditSource,
  premiumSourceToAuditSource,
} from "./financialModelAuditTypes";
import { GroupFlow, PerilTerms } from "./financialModelAudit";
import { SourceBadge } from "./SourceBadge";
import styles from "./financialModelAudit.module.css";

type FinancialModelAuditDialogProps = {
  reportId: string;
  layer: client.Layer | null;
  // This layer's premium breakdown. Its resolved source per peril tells us
  // which modeler is currently enabled for each peril.
  layerBreakdown: client.LayerPremiumBreakdown | undefined;
  onHide: () => void;
};

// Peril types priced by the rater rather than a cat model. They never have a
// financial-model audit, so the breakdown skips them.
const NON_CAT_PERIL_TYPES = new Set<string>(["aop", "equipment_breakdown"]);

const PerilSection: React.FC<{
  peril: client.FinancialModelAuditPeril;
  source: FinancialModelAuditSource;
  groups: client.FinancialModelAuditGroup[];
}> = ({ peril, source, groups }) => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.perilName}>
          {perilCodeDisplayName(peril.peril)}
          <SourceBadge source={source} />
        </span>
        <span className={styles.periodNote}>
          {peril.total_periods.toLocaleString()} simulated years
        </span>
      </div>

      <PerilTerms peril={peril} showFunctions />

      {groups.map((group) => (
        <GroupFlow key={group.limit_zone} group={group} />
      ))}
    </div>
  );
};

type Entry = {
  peril: client.FinancialModelAuditPeril;
  source: FinancialModelAuditSource;
  groups: client.FinancialModelAuditGroup[];
};

export const FinancialModelAuditDialog: React.FC<
  FinancialModelAuditDialogProps
> = ({ reportId, layer, layerBreakdown, onHide }) => {
  const open = layer != null;

  // Which modeler is enabled for each peril code on this layer (from the
  // premium breakdown's resolved source). Perils resolved to the blended
  // source have no single underlying run, so they're tracked separately.
  const perilCodeToSource = new Map<string, FinancialModelAuditSource>();
  const blendedPerilCodes = new Set<string>();
  if (layerBreakdown) {
    for (const breakdown of Object.values(layerBreakdown.peril_types)) {
      // AOP and equipment breakdown are priced by the rater, not the cat
      // model, so they never produce an AAL / audit. Skip them rather than
      // report them as "no audit available".
      if (NON_CAT_PERIL_TYPES.has(breakdown.resolved.peril_type)) continue;
      const auditSource = premiumSourceToAuditSource(breakdown.resolved.source);
      for (const part of breakdown.resolved.parts) {
        if (auditSource) {
          perilCodeToSource.set(part.peril_code, auditSource);
        } else {
          blendedPerilCodes.add(part.peril_code);
        }
      }
    }
  }
  const neededSources = new Set(perilCodeToSource.values());

  // One query per source (order fixed so hook order is stable); only the
  // sources actually used on this layer are enabled.
  const rms = useFinancialModelAudit(
    reportId,
    "rms",
    open && neededSources.has("rms")
  );
  const air = useFinancialModelAudit(
    reportId,
    "air",
    open && neededSources.has("air")
  );
  const spatialkat = useFinancialModelAudit(
    reportId,
    "spatialkat",
    open && neededSources.has("spatialkat")
  );
  const rms22 = useFinancialModelAudit(
    reportId,
    "rms_22",
    open && neededSources.has("rms_22")
  );

  const auditBySource: Record<
    FinancialModelAuditSource,
    client.FinancialModelAudit | null | undefined
  > = {
    rms: rms.data,
    air: air.data,
    spatialkat: spatialkat.data,
    rms_22: rms22.data,
  };
  const queries = [rms, air, spatialkat, rms22];
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);

  // For each peril, pull its waterfall from the audit of its enabled source.
  const entries: Entry[] = [];
  if (layer) {
    for (const source of ALL_AUDIT_SOURCES) {
      const audit = auditBySource[source];
      if (!audit) continue;
      for (const peril of audit.perils) {
        if (perilCodeToSource.get(peril.peril) !== source) continue;
        const groups =
          peril.layers.find((l) => l.layer_id === layer.layer_id)?.groups ?? [];
        if (groups.length > 0) {
          entries.push({ peril, source, groups });
        }
      }
    }
  }
  entries.sort((a, b) =>
    perilCodeDisplayName(a.peril.peril).localeCompare(
      perilCodeDisplayName(b.peril.peril)
    )
  );

  // Perils that are enabled on the layer but have no audit to show (blended
  // source, or a run that predates audit capture).
  const shownCodes = new Set<string>(entries.map((e) => e.peril.peril));
  const unavailable = Array.from(
    new Set([
      ...Array.from(perilCodeToSource.keys()).filter((c) => !shownCodes.has(c)),
      ...blendedPerilCodes,
    ])
  ).map(perilCodeDisplayName);

  return (
    <Dialog
      header={
        layer ? `Model breakdown — ${readableLayer(layer)}` : "Model breakdown"
      }
      visible={open}
      onHide={onHide}
      style={{ width: "min(760px, 94vw)" }}
      dismissableMask
    >
      <div className={styles.container}>
        <p className={styles.intro}>
          How each peril&apos;s ground-up loss flows down to the gross loss
          entering this layer — averaged per year, using the modeler currently
          enabled for each peril.
        </p>

        {isFetching && entries.length === 0 ? (
          <div className={styles.loading}>
            <ProgressSpinner
              style={{ width: 36, height: 36 }}
              strokeWidth="4"
            />
          </div>
        ) : isError ? (
          <Message
            severity="error"
            text="Could not load the model breakdown."
          />
        ) : (
          <>
            {entries.map(({ peril, source, groups }) => (
              <PerilSection
                key={`${source}:${peril.peril}`}
                peril={peril}
                source={source}
                groups={groups}
              />
            ))}
            {entries.length === 0 && unavailable.length === 0 && (
              <Message
                severity="info"
                text="No model audit is available for this layer yet."
              />
            )}
            {unavailable.length > 0 && (
              <Message
                severity="info"
                text={`No audit to show for: ${unavailable.join(", ")}. The run may predate audit capture or use a blended source.`}
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
